// File Name: ServicesGrid.jsx

import React from "react";

import { Link } from "react-router-dom";

import {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
  ArrowUpRight,
} from "lucide-react";

import { SERVICES } from "@/lib/site";

const ICONS = {
  Cpu,
  Users,
  LineChart,
  HeartHandshake,
};

function ServiceCard({ s, index }) {

  const Icon = ICONS[s.icon] || Cpu;

  return (
    <div
      className="
        service-card
        group
        relative

        bg-white/70
        backdrop-blur-2xl

        border
        border-purple-100/60

        rounded-[28px]
        md:rounded-[32px]

        p-5
        sm:p-6
        md:p-8

        overflow-hidden

        hover:-translate-y-2
        md:hover:-translate-y-3

        hover:border-purple-200

        transition-all
        duration-500

        shadow-[0_10px_40px_rgba(120,70,180,0.08)]

        hover:shadow-[0_25px_70px_rgba(120,70,180,0.16)]
      "
      style={{
        animationDelay: `${index * 0.12}s`,
      }}
    >

      {/* MOBILE ANIMATION STYLE */}

      <style>
        {`
          @keyframes mobileCardReveal {
            0% {
              opacity: 0;
              transform:
                translateY(40px)
                scale(0.96);
            }

            60% {
              opacity: 1;
              transform:
                translateY(-6px)
                scale(1.01);
            }

            100% {
              opacity: 1;
              transform:
                translateY(0)
                scale(1);
            }
          }

          @keyframes floatingGlow {
            0% {
              transform: translateY(0px);
            }

            50% {
              transform: translateY(-6px);
            }

            100% {
              transform: translateY(0px);
            }
          }

          @media (max-width: 768px) {

            .service-card {
              animation:
                mobileCardReveal 0.9s
                cubic-bezier(0.22, 1, 0.36, 1)
                forwards;
            }

            .service-icon {
              animation:
                floatingGlow 3s ease-in-out infinite;
            }
          }
        `}
      </style>

      {/* BACKGROUND GLOW */}

      <div
        className="
          absolute

          top-0
          right-0

          w-32
          md:w-40

          h-32
          md:h-40

          bg-purple-300/20

          blur-3xl

          rounded-full

          opacity-0

          group-hover:opacity-100

          transition-all
          duration-500
        "
      />

      {/* SECOND GLOW */}

      <div
        className="
          absolute

          bottom-0
          left-0

          w-28
          md:w-32

          h-28
          md:h-32

          bg-fuchsia-300/15

          blur-3xl

          rounded-full

          opacity-0

          group-hover:opacity-100

          transition-all
          duration-500
        "
      />

      {/* ICON */}

      <div
        className="
          service-icon

          relative

          w-14
          md:w-16

          h-14
          md:h-16

          rounded-2xl

          bg-gradient-to-br
          from-[#6D3B8C]
          via-[#9B4D96]
          to-[#C67BCF]

          flex
          items-center
          justify-center

          mb-6
          md:mb-7

          shadow-lg
          shadow-purple-500/20

          transition-all
          duration-500

          group-hover:scale-110
        "
      >

        {/* INNER LIGHT */}

        <div
          className="
            absolute inset-0

            rounded-2xl

            bg-white/10

            blur-xl
          "
        />

        <Icon
          className="
            relative

            w-6
            md:w-7

            h-6
            md:h-7

            text-white
          "
        />

      </div>

      {/* TITLE */}

      <h3
        className="
          text-xl
          sm:text-2xl

          font-bold

          text-slate-900

          mb-4

          leading-tight
        "
        style={{
          fontFamily: "Sora",
        }}
      >

        {s.title}

      </h3>

      {/* DESCRIPTION */}

      <p
        className="
          text-sm
          sm:text-base

          text-slate-600

          leading-relaxed

          mb-7
        "
      >

        {s.short}

      </p>

      {/* FEATURES */}

      <ul className="space-y-3 mb-8">

        {s.items
          .slice(0, 4)
          .map((item) => (

            <li
              key={item}

              className="
                text-sm

                text-slate-600

                flex
                items-center

                gap-3
              "
            >

              <span
                className="
                  w-2.5
                  h-2.5

                  rounded-full

                  bg-gradient-to-r
                  from-purple-500
                  to-fuchsia-400

                  shadow-[0_0_12px_rgba(168,85,247,0.5)]
                "
              />

              {item}

            </li>

          ))}

      </ul>

      {/* BUTTON */}

      <Link
        to={`/services/${s.slug}`}

        className="
          inline-flex
          items-center

          gap-2

          text-purple-700

          font-semibold

          hover:gap-3

          transition-all
          duration-300
        "
      >

        Learn More

        <ArrowUpRight
          className="
            w-4
            h-4
          "
        />

      </Link>

    </div>
  );
}

export default function ServicesGrid() {

  return (
    <section

      className="
        relative

        py-16
        md:py-24
        lg:py-28

        overflow-hidden

        bg-gradient-to-b
        from-[#F8F4FB]
        via-white
        to-[#F5EEFA]
      "
    >

      {/* TOP GLOW */}

      <div
        className="
          absolute

          top-20
          left-[-120px]

          w-[300px]
          md:w-[460px]

          h-[300px]
          md:h-[460px]

          bg-purple-300/15

          rounded-full

          blur-[130px]
        "
      />

      {/* RIGHT GLOW */}

      <div
        className="
          absolute

          bottom-0
          right-[-120px]

          w-[260px]
          md:w-[420px]

          h-[260px]
          md:h-[420px]

          bg-fuchsia-300/12

          rounded-full

          blur-[130px]
        "
      />

      {/* GRID OVERLAY */}

      <div
        className="
          absolute inset-0

          grid-bg

          opacity-[0.04]
        "
      />

      <div
        className="
          relative

          max-w-7xl
          mx-auto

          px-4
          sm:px-6
          md:px-8
        "
      >

        {/* HEADER */}

        <div
          className="
            text-center

            max-w-3xl

            mx-auto

            mb-14
            md:mb-20
          "
        >

          <div
            className="
              text-purple-600

              uppercase

              tracking-[0.28em]
              md:tracking-[0.35em]

              text-xs
              md:text-sm

              font-bold

              mb-4
            "
          >

            OUR SERVICES

          </div>

          <h2
            className="
              text-3xl
              sm:text-4xl
              md:text-5xl

              font-bold

              text-slate-900

              leading-tight
            "
            style={{
              fontFamily: "Sora",
            }}
          >

            Smart solutions for
            <br />

            modern businesses

          </h2>

          <p
            className="
              mt-6

              text-slate-600

              text-base
              md:text-lg

              leading-relaxed
            "
          >

            Enterprise-grade services crafted
            to help businesses scale,
            innovate and lead confidently.

          </p>

        </div>

        {/* GRID */}

        <div
          className="
            grid

            grid-cols-1
            sm:grid-cols-2
            xl:grid-cols-4

            gap-6
            md:gap-8
          "
        >

          {SERVICES.map((service, index) => (

            <ServiceCard
              key={service.slug}
              s={service}
              index={index}
            />

          ))}

        </div>

      </div>

    </section>
  );
}